import { useCallback, useEffect, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, EXPENSE_CATEGORIES, fmtErr, GST_RATES, inr, today, TXN_CATEGORIES } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

export default function Expenses() {
  const [docs, setDocs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ date: today(), category: "Rent", vendor_name: "", amount: "", gst_rate: "0", account_id: "", description: "" });
  const [settings, setSettings] = useState(null);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");

  const categories = [
    ...EXPENSE_CATEGORIES,
    ...(settings?.expense_categories || []).filter((c) => !EXPENSE_CATEGORIES.includes(c)),
  ];

  const load = useCallback(async () => {
    try {
      const [e, a, s] = await Promise.all([API.get("/expenses"), API.get("/banking/accounts"), API.get("/settings")]);
      setDocs(e.data);
      setAccounts(a.data);
      setSettings(s.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      await API.post("/expenses", {
        date: form.date,
        category: form.category,
        vendor_name: form.vendor_name,
        amount: Number(form.amount),
        gst_rate: Number(form.gst_rate),
        account_id: form.account_id || null,
        description: form.description,
      });
      toast.success("Expense recorded");
      setOpen(false);
      setForm({ date: today(), category: "Rent", vendor_name: "", amount: "", gst_rate: "0", account_id: "", description: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await API.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const saveCategory = async () => {
    const name = newCat.trim();
    if (!name) return toast.error("Enter a category name");
    if (categories.some((c) => c.toLowerCase() === name.toLowerCase())) {
      setForm((f) => ({ ...f, category: categories.find((c) => c.toLowerCase() === name.toLowerCase()) }));
      setAddCatOpen(false);
      setNewCat("");
      return;
    }
    try {
      await API.put("/settings", { ...settings, expense_categories: [...(settings?.expense_categories || []), name] });
      setSettings((s) => ({ ...s, expense_categories: [...(s?.expense_categories || []), name] }));
      setForm((f) => ({ ...f, category: name }));
      toast.success(`Category "${name}" added`);
      setAddCatOpen(false);
      setNewCat("");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Expenses</h1>
          <p className="text-sm text-muted-foreground">Track business spending with optional GST input credit</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-expense-button"><Plus size={16} className="mr-1" weight="bold" /> New Expense</Button>
          </DialogTrigger>
          <DialogContent data-testid="expense-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Record Expense</DialogTitle>
              <DialogDescription className="sr-only">Record a business expense with optional GST</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={set("date")} data-testid="expense-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => (v === "__add__" ? setAddCatOpen(true) : set("category")(v))}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__add__" data-testid="expense-category-add-option">+ Add new category…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor / Payee</Label>
                <Input value={form.vendor_name} onChange={set("vendor_name")} placeholder="Optional" data-testid="expense-vendor-input" />
              </div>
              <div className="space-y-2">
                <Label>Amount (₹, excl. GST)</Label>
                <Input type="number" min="0" step="any" value={form.amount} onChange={set("amount")} data-testid="expense-amount-input" />
              </div>
              <div className="space-y-2">
                <Label>GST %</Label>
                <Select value={form.gst_rate} onValueChange={set("gst_rate")}>
                  <SelectTrigger data-testid="expense-gst-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((g) => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Paid from account</Label>
                <Select value={form.account_id} onValueChange={set("account_id")}>
                  <SelectTrigger data-testid="expense-account-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={set("description")} placeholder="What was this for?" data-testid="expense-description-input" />
              </div>
            </div>
            {form.amount && (
              <p className="text-sm text-muted-foreground">
                Total with GST: <span className="font-semibold tabular-nums text-foreground">{inr(Number(form.amount) * (1 + Number(form.gst_rate) / 100))}</span>
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="expense-cancel-button">Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="expense-save-button">{saving ? "Saving…" : "Save Expense"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border bg-card">
        {docs.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <p className="font-heading text-lg font-semibold">No expenses recorded</p>
            <p className="mt-1 text-sm text-muted-foreground">Rent, salaries, fuel — record them here to keep your P&L accurate.</p>
          </div>
        ) : (
          <Table data-testid="expenses-table">
            <TableHeader>
              <TableRow>
                <TableHead>No.</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">GST</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id} className="transition-colors hover:bg-muted/50" data-testid={`expense-row-${d.number}`}>
                  <TableCell className="font-medium">{d.number}</TableCell>
                  <TableCell>{d.date}</TableCell>
                  <TableCell>{d.category}</TableCell>
                  <TableCell>{d.vendor_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(d.amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(d.gst_amount)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{inr(d.total)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => del(d.id)} data-testid={`expense-delete-${d.number}`}>
                      <Trash size={16} className="text-muted-foreground" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={addCatOpen} onOpenChange={setAddCatOpen}>
        <DialogContent className="max-w-sm" data-testid="expense-add-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">New Expense Category</DialogTitle>
            <DialogDescription className="sr-only">Add a custom expense category saved to your settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Salary Advance" data-testid="expense-new-category-input" />
            </div>
            <Button className="w-full" onClick={saveCategory} data-testid="expense-add-category-save">Add & Select</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
